import * as allCurves from "@visx/curve"
import { scaleBand, scaleLinear } from "@visx/scale"
import XAxis from "./XAxis"
import { getGCFOibiaXaxis, getNonGCFOibiaXaxis, getYscale } from "./utils"
import { LinePath } from "@visx/shape"
import { useTooltip, useTooltipInPortal, defaultStyles } from "@visx/tooltip"
import { localPoint } from "@visx/event"
import YAxis from "./YAxis"
import { GridRows } from "@visx/grid"
import TooltipContent from "./TooltipContent"
import { useMemo } from "react"
import { getPalette } from "../palette"

const tooltipStyles = {
  ...defaultStyles,
  minWidth: 60,
  backgroundColor: "var(--primary-main-dark-green)",
  color: "white",
  zIndex: 9999999,
  border: "2px solid var(--primary-main-dark-green)",
  borderRadius: "10px",
}

export type TooltipInfo = {
  key: string
  value: number
  title: string
  entity: string
}

export type TooltipData = {
  info: TooltipInfo
  hoveredIndex: number
}
type ChartDataType = {
  key: string
  entity: string
  title: string
  value: number
}
export default function LineChart({
  width,
  height,
  revenueData,
  oibiaData,
  bottomTitles,
  keys,
  availableEntities,
  entityIndex,
  dataType,
  showOI,
  showRevenue,
  entity,
}: {
  width: number
  height: number
  revenueData: ChartDataType[][]
  oibiaData: ChartDataType[][]
  bottomTitles: string[]
  keys: string[]
  availableEntities: string[][]
  entityIndex: number
  dataType: string
  showOI: boolean
  showRevenue: boolean
  entity: string
}) {
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip,
  } = useTooltip<TooltipData>()
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
  })

  const hasAvailableEntities = !!availableEntities[entityIndex].length

  const revenueValues = revenueData
    .map((value) => value)
    .flat()
    .filter((x: { entity: string }) =>
      availableEntities[entityIndex].includes(x.entity),
    )

  const oibiaValues = oibiaData
    .map((value) => value)
    .flat()
    .filter((x: { entity: string }) =>
      availableEntities[entityIndex].includes(x.entity),
    )

  const minRevenueData = Math.min(
    ...revenueValues.map((o: { value: number }) => o.value),
  )
  const maxRevenueData = Math.max(
    ...revenueValues.map((o: { value: number }) => o.value),
  )

  const minOibiaData = Math.min(
    ...oibiaValues.map((o: { value: number }) => o.value),
  )
  const maxOibiaData = Math.max(
    ...oibiaValues.map((o: { value: number }) => o.value),
  )

  const defaultConfig = {
    top: 50,
  }

  const xScale = useMemo(
    () =>
      scaleBand<string>({
        domain: keys,
        range: [width / 5, width + width / 5],
      }),
    [keys, width],
  )

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: getYscale(
          showRevenue,
          showOI,
          maxRevenueData,
          maxOibiaData,
          minRevenueData,
          minOibiaData,
          hasAvailableEntities,
        ),

        nice: true,
        range: [0, height / 1.3],
      }),
    [
      hasAvailableEntities,
      height,
      maxOibiaData,
      maxRevenueData,
      minOibiaData,
      minRevenueData,
      showOI,
      showRevenue,
    ],
  )

  return (
    <div key={entityIndex}>
      <svg ref={containerRef} width={width} height={height}>
        <GridRows scale={yScale} numTicks={5} width={width} top={50} left={75}>
          {(lines) => {
            return lines.lines.map((line, index) => (
              <line
                key={index}
                x1="0"
                x2={width}
                y1={line.from.y}
                y2={line.to.y}
                stroke={line.from.y! > yScale(0) ? "var(--other-red)" : "grey"}
                strokeWidth={line.from.y! === yScale(0) ? 1 : 0.5}
                strokeLinecap="round"
              />
            ))
          }}
        </GridRows>
        {showOI &&
          oibiaData.map((x: ChartDataType[], itemIndex: number) => (
            <g key={itemIndex}>
              {x.map(
                (item, i) =>
                  !!availableEntities[entityIndex].includes(item.entity) &&
                  i === 0 && (
                    <LinePath
                      key={itemIndex + i}
                      curve={allCurves["curveLinear"]}
                      data={x}
                      x={(d) =>
                        xScale(
                          dataType === "non-gcf"
                            ? getNonGCFOibiaXaxis(d.key)
                            : getGCFOibiaXaxis(d.key),
                        ) || 0
                      }
                      y={(d) => yScale(d.value) + defaultConfig.top}
                      stroke={getPalette(entity)[itemIndex]}
                      strokeDasharray={4}
                    />
                  ),
              )}
            </g>
          ))}

        {showRevenue &&
          revenueData.map((x: ChartDataType[], itemIndex: number) =>
            x.map(
              (item, i) =>
                !!availableEntities[entityIndex].includes(item.entity) &&
                i === 0 && (
                  <LinePath
                    key={itemIndex + i}
                    curve={allCurves["curveLinear"]}
                    data={x}
                    x={(d) => xScale(d.key) || 0}
                    y={(d) => yScale(d.value) + defaultConfig.top}
                    stroke={getPalette(entity)[itemIndex]}
                  />
                ),
            ),
          )}
        {showRevenue &&
          revenueData.map((x: ChartDataType[], itemIndex: number) =>
            x.map(
              (item, i) =>
                !!availableEntities[entityIndex].includes(item.entity) && (
                  <g>
                    <circle
                      key={`${itemIndex}- ${i}`}
                      r={4}
                      cx={xScale?.(item.key)}
                      cy={yScale?.(item.value) + defaultConfig.top}
                      fill={getPalette(entity)[itemIndex]}
                      onMouseMove={(event) => {
                        const eventSvgCoords = localPoint(event)
                        showTooltip({
                          tooltipData: {
                            info: {
                              key: item.key,
                              value: item.value,
                              title: item.title,
                              entity: item.entity,
                            },
                            hoveredIndex: i,
                          },
                          tooltipTop: eventSvgCoords?.y,
                          tooltipLeft: eventSvgCoords?.x,
                        })
                      }}
                      onMouseLeave={() => hideTooltip()}
                    />
                  </g>
                ),
            ),
          )}
        {showOI &&
          oibiaData.map((x: ChartDataType[], itemIndex: number) => (
            <>
              {x.map(
                (item, i) =>
                  !!availableEntities[entityIndex].includes(item.entity) && (
                    <>
                      <g>
                        <circle
                          key={`${itemIndex}- ${i}`}
                          r={4}
                          cx={xScale?.(
                            dataType === "non-gcf"
                              ? getNonGCFOibiaXaxis(item.key)
                              : getGCFOibiaXaxis(item.key),
                          )}
                          cy={yScale?.(item.value) + defaultConfig.top}
                          fill="white"
                          onMouseMove={(event) => {
                            const eventSvgCoords = localPoint(event)
                            showTooltip({
                              tooltipData: {
                                info: {
                                  key: item.key,
                                  value: item.value,
                                  title: item.title,
                                  entity: item.entity,
                                },
                                hoveredIndex: i,
                              },
                              tooltipTop: eventSvgCoords?.y,
                              tooltipLeft: eventSvgCoords?.x,
                            })
                          }}
                          onMouseLeave={() => hideTooltip()}
                        />
                      </g>
                    </>
                  ),
              )}
            </>
          ))}
        <YAxis scale={yScale} />
        {hasAvailableEntities && (
          <XAxis
            scale={xScale}
            left={width / 4.5 - width / 2.45}
            top={height / 1.15}
            bottomTitles={bottomTitles}
          />
        )}
      </svg>
      {tooltipOpen && tooltipData && tooltipData.info.key !== "Global" && (
        <TooltipInPortal
          top={tooltipTop}
          left={tooltipLeft}
          style={tooltipStyles}
        >
          <TooltipContent data={tooltipData.info} />
        </TooltipInPortal>
      )}
    </div>
  )
}
